<template>
  <Panel>
    <div class="overview">
      <div
        v-if="shaftCondition && !loading"
        class="overview__container"
      >
        <!-- Shaft name -->
        <div class="overview__description typo-super-tiny">
          {{ $t('shafts.dashboard.condition_overview') }}
        </div>
        <h1 class="typo-h1 overview__title">
          {{ shaftCondition.name }}
        </h1>
        <!-- Shaft condition details -->
        <div class="overview__items">
          <ConditionItem
            :description="$t('shafts.dashboard.shaft_condition')"
            :value="shaftCondition.condition_rating"
          />
          <ConditionItem
            :description="$t('shafts.dashboard.risk_rating')"
            risk
            :value="shaftCondition.risk_rating"
          />
          <ConditionItem
            :date="shaftCondition?.last_inspection_date || '-'"
            :description="$t('shafts.dashboard.last_inspection')"
            :showIndicator="false"
          />
        </div>
        <div class="overview__warnings">
          <router-link
            v-if="shaftCondition.inspections_awaiting_approval_count > 0"
            class="overview__warnings-link"
            :to="{ name: 'shaftInspections', params: { scope: 'inspections' } }"
          >
            <OverviewWarning
              :count="shaftCondition.inspections_awaiting_approval_count"
              :label="awaitingApprovalLabel"
              :modifiers="['flat', 'inline']"
              :notice="true"
            />
          </router-link>
          <div class="overview__warnings-title typo-super-tiny">
            {{ $t('shafts.dashboard.warnings_breakdown') }}
            ({{ shaftCondition.number_of_warnings }})
          </div>
          <OverviewWarning
            :count="shaftCondition.members_requiring_maintenance_count"
            :label="$t('shafts.dashboard.warning_maintenance')"
          />
          <OverviewWarning
            :count="shaftCondition.overdue_jobs_count"
            :label="$t('shafts.dashboard.overdue_jobs')"
          />
        </div>
      </div>
      <Loader
        v-if="loading"
        class="overview__loader"
      />
      <!-- Shaft cross section -->
      <div class="overview-diagram">
        <Loader
          v-if="loadingDiagram || !shaft"
          class="overview__loader"
        />
        <ShaftDiagram
          v-else
          :elements="elements"
          :modifiers="['small']"
          :shape="shaft.shape"
        />
      </div>
    </div>
  </Panel>
</template>

<script setup>
import { onMounted, ref, watch, computed } from 'vue';
import I18n from 'i18n-js';
import useAjax from '@/functions/use_ajax';
import { useShaft } from '@/functions/use_remote_object';
import truncateNumber from '@/functions/simm/truncate_number';

import ConditionItem from '@/components/shafts/dashboard/condition_item.vue';
import ShaftDiagram from '@/components/shafts/shaft_diagram.vue';
import Loader from '@/components/generic/loader.vue';
import Panel from '@/components/dashboard/panel.vue';
import OverviewWarning from '@/components/shafts/dashboard/condition_overview_warning.vue';

const props = defineProps({
  organisationId: { type: String, required: true },
  shaftId: { type: String, required: true },
});

const ajax = useAjax();
const shaft = useShaft(props);

const loading = ref(false);
const loadingDiagram = ref(true);
const shaftCondition = ref(null);
const elements = ref(null);

onMounted(() => {
  loadShaftData();
});

/**
 * Label for awaiting approval
 */
const awaitingApprovalLabel = computed(() => {
  if (shaftCondition.value?.inspections_awaiting_approval_count === 1) {
    return I18n.t('shafts.dashboard.inspection_waiting_approval');
  } else {
    return I18n.t('shafts.dashboard.inspections_waiting_approval_plural');
  }
});

const loadShaftData = () => {
  loadShaftCondition();
  loadShaftDiagram();
};

/**
 * Get shaft condition data
 */
const loadShaftCondition = async () => {
  loading.value = true;

  let { ok, data } = await ajax(`/organisations/${props.organisationId}/shafts/${props.shaftId}/dashboard/condition_overview`);

  if (ok) {
    shaftCondition.value = data;
    shaftCondition.value.condition_rating = truncateNumber(shaftCondition.value.condition_rating);
    shaftCondition.value.risk_rating = truncateNumber(shaftCondition.value.risk_rating);
  }

  loading.value = false;
};

/**
 * Get shaft cross-section diagram data
 */
const loadShaftDiagram = async () => {
  loadingDiagram.value = true;

  let { ok, data } = await ajax(`/organisations/${props.organisationId}/shafts/${props.shaftId}/dashboard/geometry`);

  if (ok) {
    elements.value = data;
  }

  loadingDiagram.value = false;
};

/**
 * Watch for change on shaftID and get shaft condition
 */
watch(() => props.shaftId, loadShaftData);
</script>

<style scoped lang="scss">
@import '@/stylesheets/global';
.overview {
  padding: bu(2.5) bu(1) bu(1);

  @include breakpoint(large) {
    padding: bu(2.5) bu(3);
    display: grid;
    gap: bu(3);
    grid-template-columns: 1fr 1fr;
  }

  h1 {
    color: map-get($foreground-color, light);
    font-style: normal;
    font-weight: normal;
  }

  &__description {
    color: transparentize(map-get($foreground-color, default), 0.5);
    text-transform: uppercase;
  }

  &__items {
    display: grid;
    gap: bu(2);
    grid-template-columns: 1fr 1fr;
    margin-top: bu(3.5);

    @include breakpoint(medium) {
      grid-template-columns: 1fr 1fr 1fr;
    }
  }

  &__warnings {
    border-top: 1px solid map-get($foreground-color, darker);
    margin-top: bu(3);
    padding-top: bu(3);
  }

  &__warnings-title {
    color: transparentize(map-get($foreground-color, default), 0.5);
    margin: bu(2) 0;
    text-transform: uppercase;
  }

  &__warnings-link {
    display: inline-block;
  }

  &__loader {
    min-height: rem(370);
  }
}
</style>
